/**
 * drilldown module
 * @module twt.drilldown
 * @requires twt.media-query
 */

import * as TWTmediaQuery from './twt.media-query';

/**
 * Drilldown menu
 *
 * @export
 * @class Drilldown
 */
export default class Drilldown {
  /**
   * Creates a new instance of a drilldown menu.
   * @param {HTMLElement} element - HTMLElement to make into an drilldown menu.
   * @param {Object} options - Overrides for the default settings.
   */
  constructor(element, options) {
    this.element = element;
    this.options = Object.assign(Drilldown.defaults, options);
    this.level = 0;
    this.clickstream = [];
    this.menu = null;

    // set level
    this.element.dataset.level = this.level;

    // initialize
    this.init();
  }

  /**
   * @return {void}
   */
  init() {
    const currentSize = TWTmediaQuery.getCurrentSize();
    if (currentSize === 'm' || currentSize === 's' || currentSize === 'xs') {
      this.sublevels = this.element.querySelectorAll('[data-sublevel]');

      this.registerEventListener();
      this.size = currentSize;
    }

    window.addEventListener('changed.twt.mediaQuery', () => {
      this.handleMediaQuery(event);
    });
  }

  /**
   * @param  {Event} event click event
   * @return {void}
   */
  navigate(event) {
    const currentSize = TWTmediaQuery.getCurrentSize();

    if (currentSize === 'm' || currentSize === 's' || currentSize === 'xs') {
      const customEvent = document.createEvent('Event');
      const listItem = event.target.closest('li');

      // set previous sublevel
      if (this.sublevel !== undefined) this.previousSublevel = this.sublevel;

      // set sublevel
      if (listItem !== null) {
        this.sublevel = listItem.querySelector('[data-sublevel]');
      }

      // If it was a menu item
      if (event.target && event.target.dataset.forward !== undefined) {
        if (event.preventDefault) {
          event.preventDefault();
        } else {
          event.returnValue = false; // eslint-disable-line no-param-reassign
        }
        event.stopImmediatePropagation();

        // initialize custom event
        customEvent.initEvent('forward.twt.drilldown', false, true);

        // dispatch event
        this.element.dispatchEvent(customEvent);
      } else if (event.target && event.target.dataset.back !== undefined) {
        // initialize custom event
        customEvent.initEvent('back.twt.drilldown', false, true);

        // dispatch event
        this.element.dispatchEvent(customEvent);
      }
    }
  }

  /**
   * @param  {Event} event changed.twt.mediaQuery
   * @return {void}
   */
  handleMediaQuery(event) {
    const newSize = event.detail.newSize;
    if (newSize !== 'm' && newSize !== 's' && newSize !== 'xs') {
      this.destroy();
    } else {
      this.init();
    }

    this.currentSize = newSize;
  }

  /**
   * @return {void}
   */
  registerEventListener() {
    this.element.addEventListener('click', (event) => {
      this.navigate(event);
    });

    this.element.addEventListener('forward.twt.drilldown', () => {
      this.forward();
    });

    this.element.addEventListener('back.twt.drilldown', () => {
      this.back();
    });
  }

  /**
   * @return {void}
   */
  deregisterEventListener() {
    this.element.removeEventListener('forward.twt.drilldown', () => {});
    this.element.removeEventListener('back.twt.drilldown', () => {});
    window.removeEventListener('changed.twt.mediaQuery', () => {});
  }

  /**
   * @return {void}
   */
  forward() {
    // set level
    this.level += 1;
    this.element.setAttribute('data-level', this.level);
    // reset active menu
    if (this.menu !== null) this.menu.classList.remove(this.options.activeClass);
    this.menu = this.sublevel.querySelector('.menu');

    // show sublevel
    this.sublevel.classList.add(this.options.activeClass);
    this.menu.classList.add(this.options.activeClass);

    // add to clickstream
    this.clickstream.push(this.sublevel);
  }

  /**
   * @return {void}
   */
  back() {
    // set level
    this.level -= 1;
    this.element.setAttribute('data-level', this.level);

    // reset active menu
    this.menu.classList.remove(this.options.activeClass);
    this.menu = this.sublevel.querySelector('.menu');
    if (this.menu !== null) this.menu.classList.add(this.options.activeClass);

    // hide sublevel
    this.clickstream[this.clickstream.length - 1].classList.remove(this.options.activeClass);

    // remove last entry
    this.clickstream.pop();
  }

  /**
   * @return {void}
   */
  destroy() {
    this.deregisterEventListener();
  }
}

Drilldown.defaults = {
  activeClass: 'is--active',
  visibleClass: 'is--shown',
};



// WEBPACK FOOTER //
// ./assets/js/components/twt.drilldown.js